/**
 * Picker handlers for Edit Sent form (search, selection, add/delete texts).
 */

import { useCallback, type Dispatch, type SetStateAction } from 'react';

import { MAX_SELECTED_TEXTS, parseSmsTextsInput } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm/shared/PrepareNewDropsForm.utils';

import type { EditSentDropCreativeAiApi } from './useEditSentDropCreativeAiState';

function uniqueMerge<T>(a: T[], b: T[]): T[] {
  return Array.from(new Set([...a, ...b]));
}

interface Args {
  sendersList: string[];
  textsList: string[];
  setSelectedSenderNames: Dispatch<SetStateAction<string[]>>;
  setSelectedSmsTexts: Dispatch<SetStateAction<string[]>>;
  senderSearch: string;
  setSenderSearch: Dispatch<SetStateAction<string>>;
  textSearch: string;
  setTextSearch: Dispatch<SetStateAction<string>>;
  newSenderValue: string;
  setNewSenderValue: Dispatch<SetStateAction<string>>;
  newTextValue: string;
  setNewTextValue: Dispatch<SetStateAction<string>>;
  setLocalSenders: Dispatch<SetStateAction<string[]>>;
  setLocalTexts: Dispatch<SetStateAction<string[]>>;
  onAddSender?: (name: string) => Promise<void>;
  onAddText?: (texts: string[]) => Promise<void>;
  onDeleteText?: (text: string) => Promise<void>;
  onDeleteTextsBulk?: (texts: string[]) => Promise<void>;
  creativeAi: EditSentDropCreativeAiApi;
}

export function useEditSentDropPickers(a: Args) {
  const {
    sendersList,
    textsList,
    setSelectedSenderNames,
    setSelectedSmsTexts,
    senderSearch,
    setSenderSearch,
    textSearch,
    setTextSearch,
    newSenderValue,
    setNewSenderValue,
    newTextValue,
    setNewTextValue,
    setLocalSenders,
    setLocalTexts,
    onAddSender,
    onAddText,
    onDeleteText,
    onDeleteTextsBulk,
    creativeAi,
  } = a;

  const selectAllSenders = useCallback(() => {
    const filtered = sendersList.filter((s) => s.toLowerCase().includes(senderSearch.toLowerCase()));
    setSelectedSenderNames((prev) => (prev.length === filtered.length ? [] : [...filtered]));
  }, [sendersList, senderSearch, setSelectedSenderNames]);
  const selectAllTexts = useCallback(() => {
    const filtered = textsList.filter((t) => t.toLowerCase().includes(textSearch.toLowerCase()));
    setSelectedSmsTexts((prev) => (prev.length === filtered.length ? [] : [...filtered]));
  }, [textsList, textSearch, setSelectedSmsTexts]);
  const { setPerDropSenderNames, setPerDropSmsTexts } = creativeAi;
  const clearSendersSelection = useCallback(() => {
    setSelectedSenderNames([]);
    setPerDropSenderNames(null);
  }, [setSelectedSenderNames, setPerDropSenderNames]);
  const clearTextsSelection = useCallback(() => {
    setSelectedSmsTexts([]);
    setPerDropSmsTexts(null);
  }, [setSelectedSmsTexts, setPerDropSmsTexts]);
  const toggleSender = useCallback(
    (name: string) =>
      setSelectedSenderNames((prev) =>
        prev.includes(name) ? prev.filter((v) => v !== name) : [...prev, name],
      ),
    [setSelectedSenderNames],
  );
  const toggleText = useCallback(
    (text: string) =>
      setSelectedSmsTexts((prev) => (prev.includes(text) ? prev.filter((v) => v !== text) : [...prev, text])),
    [setSelectedSmsTexts],
  );

  const handleAddSender = useCallback(async () => {
    const v = newSenderValue.trim();
    if (!v || !onAddSender) return;
    setSelectedSenderNames((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setLocalSenders((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setNewSenderValue('');
    await onAddSender(v);
  }, [newSenderValue, onAddSender, setNewSenderValue, setSelectedSenderNames, setLocalSenders]);

  const handleAddText = useCallback(async () => {
    if (!onAddText) return;
    const { texts } = parseSmsTextsInput(newTextValue);
    if (texts.length === 0) return;
    // All pasted texts join the library; selection stays within the send cap (as in Prepare).
    setSelectedSmsTexts((prev) => uniqueMerge(prev, texts).slice(0, MAX_SELECTED_TEXTS));
    setLocalTexts((prev) => uniqueMerge(prev, texts));
    setNewTextValue('');
    await onAddText(texts);
  }, [newTextValue, onAddText, setNewTextValue, setSelectedSmsTexts, setLocalTexts]);

  const handleDeleteText = useCallback(
    async (text: string) => {
      await onDeleteText?.(text);
      setLocalTexts((prev) => prev.filter((t) => t !== text));
    },
    [onDeleteText, setLocalTexts],
  );

  const handleDeleteTextsBulk = useCallback(
    async (texts: string[]) => {
      if (texts.length === 0) return;
      if (onDeleteTextsBulk) await onDeleteTextsBulk(texts);
      else for (const t of texts) await onDeleteText?.(t);
      const del = new Set(texts);
      setLocalTexts((prev) => prev.filter((t) => !del.has(t)));
    },
    [onDeleteText, onDeleteTextsBulk, setLocalTexts],
  );

  return {
    senderSearch,
    setSenderSearch,
    textSearch,
    setTextSearch,
    newSenderValue,
    setNewSenderValue,
    newTextValue,
    setNewTextValue,
    selectAllSenders,
    selectAllTexts,
    clearSendersSelection,
    clearTextsSelection,
    toggleSender,
    toggleText,
    handleAddSender,
    handleAddText,
    handleDeleteText,
    handleDeleteTextsBulk,
  };
}
